import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { formatKpi, Kpi } from '@utils/Formatters';
import { ValueType } from '@components/pos-fact/PosFact';
import { PerformanceType } from '@const/PerformanceType';
import { PageName } from '@utils/pageConfig/Types';

export const POS_FACT_TILE = {
  Ytd: 'YTD',
  Mat: 'MAT',
  Share: 'Share',
  Retailer: (postFix: string) => `Retailer ${postFix}`,
  Market: (postFix: string) => `Market ${postFix}`,
  RetailerShare: (postFix: string) => `Retailer ${postFix} Share`,
};

export enum PosFactTileType {
  RETAIL = 'myPerformance',
  MARKET = 'marketPerformance',
}

export enum MarketShareType {
  MY_REVENUE_SHARE = 'myRevenueShare',
  MARKET_REVENUE_SHARE = 'myRevenueGrowthVsMarket',
}

export type PosFactTileConfig = {
  kpiType: Kpi;
  tileTitle: (postFix: string) => string;
  showMarketShare?: boolean;
  tileType: PosFactTileType;
  marketShareType: MarketShareType;
  showAnnualSummary?: boolean;
  showOnlyMarketShare?: boolean;
  salesPerformanceType: PerformanceType;
};

export const POS_FACT_TILE_CONFIG: Record<string, PosFactTileConfig[] | Record<string, PosFactTileConfig[]>> = {
  [PageName.EXPLORE]: [
    {
      tileType: PosFactTileType.RETAIL,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Retailer,
      kpiType: Kpi.retailerRevenue,
      showMarketShare: true,
      salesPerformanceType: PerformanceType.Value,
    },
    {
      tileType: PosFactTileType.RETAIL,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Retailer,
      kpiType: Kpi.retailerVolume,
      showMarketShare: true,
      salesPerformanceType: PerformanceType.Volume,
    },
    {
      tileType: PosFactTileType.MARKET,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Market,
      kpiType: Kpi.marketRevenue,
      salesPerformanceType: PerformanceType.Value,
    },
    {
      tileType: PosFactTileType.MARKET,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Market,
      kpiType: Kpi.marketVolume,
      salesPerformanceType: PerformanceType.Volume,
    },
  ],
  [PageName.TRADING_ASSISTANT]: [
    {
      tileType: PosFactTileType.RETAIL,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Retailer,
      kpiType: Kpi.retailerRevenue,
      showMarketShare: true,
      salesPerformanceType: PerformanceType.Value
    },
    {
      tileType: PosFactTileType.RETAIL,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Retailer,
      kpiType: Kpi.retailerVolume,
      showMarketShare: true,
      salesPerformanceType: PerformanceType.Volume
    },
    {
      tileType: PosFactTileType.MARKET,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Market,
      kpiType: Kpi.marketRevenue,
      salesPerformanceType: PerformanceType.Value
    },
    {
      tileType: PosFactTileType.MARKET,
      marketShareType: MarketShareType.MY_REVENUE_SHARE,
      tileTitle: POS_FACT_TILE.Market,
      kpiType: Kpi.marketVolume,
      salesPerformanceType: PerformanceType.Volume
    },
  ],
  [PageName.RETAILER_OVERVIEW]: {
    [PerformanceType.Value]: [
      {
        tileType: PosFactTileType.MARKET,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.Market,
        kpiType: Kpi.marketRevenue,
        salesPerformanceType: PerformanceType.Value,
        showAnnualSummary: true,
      },
      {
        tileType: PosFactTileType.RETAIL,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.Retailer,
        kpiType: Kpi.retailerRevenue,
        salesPerformanceType: PerformanceType.Value,
        showAnnualSummary: true,
      },
      {
        tileType: PosFactTileType.RETAIL,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.RetailerShare,
        kpiType: Kpi.retailerRevenue,
        showOnlyMarketShare: true,
        salesPerformanceType: PerformanceType.Value,
        showAnnualSummary: true,
      },
    ],
    [PerformanceType.Volume]: [
      {
        tileType: PosFactTileType.MARKET,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.Market,
        kpiType: Kpi.marketVolume,
        salesPerformanceType: PerformanceType.Volume,
        showAnnualSummary: true,
      },
      {
        tileType: PosFactTileType.RETAIL,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.Retailer,
        kpiType: Kpi.retailerVolume,
        salesPerformanceType: PerformanceType.Volume,
        showAnnualSummary: true,
      },
      {
        tileType: PosFactTileType.RETAIL,
        marketShareType: MarketShareType.MY_REVENUE_SHARE,
        tileTitle: POS_FACT_TILE.RetailerShare,
        kpiType: Kpi.retailerVolume,
        showOnlyMarketShare: true,
        salesPerformanceType: PerformanceType.Volume,
        showAnnualSummary: true,
      },
    ],
  },
};

export const checkPositiveGrowth = (growth: number): boolean => {
  return Math.sign(growth || 0) === 1 || Math.sign(growth || 0) === 0;
};

export const formatGrowthValues = (growthType: GrowthTypes, type: Kpi, growth: number, currency: string): string => {
  return growthType === GrowthTypes.actual
    ? formatKpi(type, growth, {
        currencySymbol: currency,
        as: ValueType.ABSOLUTE_CHANGE,
      })
    : formatKpi(type, growth, { as: ValueType.PERCENTAGE_CHANGE });
};

export const getSalesMetrics = (
  kpiType: Kpi,
  kpiValue: number | null,
  kpiGrowth: number,
  kpiGrowthType: GrowthTypes,
  currencySymbol: string,
  showGrowthIcon: boolean = false,
): { kpiInfo: string; growthValue: string; isPositiveGrowth: boolean } => {
  const kpiInfo = formatKpi(kpiType, kpiValue, {
    currencySymbol: currencySymbol,
  });

  const growthValue = formatGrowthValues(kpiGrowthType, kpiType, kpiGrowth, currencySymbol);
  const updatedGrowthValue = showGrowthIcon ? growthValue.replace(/[+-]/g, '') : growthValue;

  const isPositiveGrowth = checkPositiveGrowth(kpiGrowth);

  return { kpiInfo, growthValue: updatedGrowthValue, isPositiveGrowth };
};

export const getPosFactTileConfig = (
  pageName: PageName,
  performanceType?: PerformanceType,
): PosFactTileConfig[] | null => {
  switch (pageName) {
    case PageName.EXPLORE:
    case PageName.TRADING_ASSISTANT:
      return POS_FACT_TILE_CONFIG[pageName] as PosFactTileConfig[];
    case PageName.RETAILER_OVERVIEW:
      return POS_FACT_TILE_CONFIG[pageName][performanceType!] as PosFactTileConfig[];
    default:
      return null;
  }
};
